import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Calendar, X, Check, ChevronDown, ChevronLeft, ChevronRight, Edit2, Trash2, Trophy, Minus, Plus, Loader2, PlayCircle, Target } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';
import { useNotification } from '../contexts/NotificationContext';
import { getTrainingPlanWeekNumber, getWeekEndDate, getWeekStartDate, parseLocalDate } from '../utils/dateUtils';
import { getWorkoutTotalVolume, getWorkoutExerciseSummary } from '../utils/exerciseUtils';
import { ExerciseDemoButton } from './ExerciseDemoButton';
import { SessionExerciseDropdown } from './SessionExerciseDropdown';
import type { TabType, Workout, CalendarDay } from '../types';

interface ExercisePRs {
  maxWeight: number;
  maxVolume: number;
  avgReps: number;
  hasHistory: boolean;
}

interface SessionSummary {
  routineName: string;
  totalVolume: number;
  completed: number;
  total: number;
  prsAchieved: { exercise: string; weight: number }[];
  volumeDelta: number | null;
}

function getExercisePRs(
  exerciseName: string,
  workouts: Workout[],
  currentDate: string
): ExercisePRs {
  const normalizedName = exerciseName.toLowerCase().trim();

  // Filter workouts from previous dates (not including current date)
  const previousWorkouts = workouts.filter(w => w.date < currentDate);

  let maxWeight = 0;
  let maxVolume = 0;
  let totalReps = 0;
  let totalSets = 0;

  for (const workout of previousWorkouts) {
    for (const exercise of workout.exercises) {
      if (exercise.name.toLowerCase().trim() === normalizedName) {
        let workoutVolume = 0;

        for (const set of exercise.sets) {
          const weight = set.weight || 0;
          const reps = set.reps || 0;

          if (reps > 0) {
            // Track max weight
            if (weight > maxWeight) {
              maxWeight = weight;
            }

            // Calculate volume for this set
            workoutVolume += weight * reps;

            // Track reps for average
            totalReps += reps;
            totalSets++;
          }
        }

        // Track max volume per workout session
        if (workoutVolume > maxVolume) {
          maxVolume = workoutVolume;
        }
      }
    }
  }

  return {
    maxWeight,
    maxVolume,
    avgReps: totalSets > 0 ? Math.round(totalReps / totalSets) : 0,
    hasHistory: totalSets > 0,
  };
}

interface LastSessionSet {
  weight: number;
  reps: number;
}

function getLastSessionSets(
  exerciseName: string,
  workouts: Workout[],
  currentDate: string
): LastSessionSet[] | null {
  const normalizedName = exerciseName.toLowerCase().trim();
  const previous = workouts
    .filter(
      (workout) =>
        workout.date < currentDate &&
        workout.exercises.some(
          (exercise) =>
            exercise.name.toLowerCase().trim() === normalizedName &&
            exercise.sets.some((set) => (set.reps || 0) > 0)
        )
    )
    .sort((a, b) => (a.date < b.date ? 1 : -1))[0];
  if (!previous) return null;
  const exercise = previous.exercises.find(
    (item) => item.name.toLowerCase().trim() === normalizedName
  );
  if (!exercise) return null;
  const sets = exercise.sets
    .filter((set) => (set.reps || 0) > 0)
    .map((set) => ({ weight: set.weight || 0, reps: set.reps || 0 }));
  return sets.length > 0 ? sets : null;
}

interface WorkoutTabProps {
  setActiveTab: (tab: TabType) => void;
}

const REST_TIMER_STORAGE_KEY = 'gym-tracker:rest-timer';

interface RestTimerState {
  total: number;
  left: number;
  endAt: number;
}

const SWIPE_REVEAL_PX = 72;

interface SwipeableSetRowProps {
  cardBg: string;
  canDelete: boolean;
  onDelete: () => void;
  totalSets: number;
  children: React.ReactNode;
}

function SwipeableSetRow({ cardBg, canDelete, onDelete, totalSets, children }: SwipeableSetRowProps) {
  const [translateX, setTranslateX] = useState(0);
  const [animate, setAnimate] = useState(true);
  const dragRef = useRef<{ startX: number; startY: number; baseX: number; axis: 'h' | 'v' | null } | null>(null);
  const isOpenRef = useRef(false);

  // Reset swipe state whenever the exercise's total set count changes (add/remove).
  // Covers: canDelete flipping false and stale state on sibling deletion.
  useEffect(() => {
    setTranslateX(0);
    isOpenRef.current = false;
    setAnimate(true);
  }, [totalSets]);

  const handleStart = (clientX: number, clientY: number) => {
    if (!canDelete) return;
    dragRef.current = {
      startX: clientX,
      startY: clientY,
      baseX: isOpenRef.current ? -SWIPE_REVEAL_PX : 0,
      axis: null,
    };
    setAnimate(false);
  };

  const handleMove = (clientX: number, clientY: number) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = clientX - drag.startX;
    const dy = clientY - drag.startY;
    if (!drag.axis) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      drag.axis = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
    }
    if (drag.axis === 'h') {
      let next = drag.baseX + dx;
      if (next > 0) next = 0;
      if (next < -SWIPE_REVEAL_PX * 1.4) next = -SWIPE_REVEAL_PX * 1.4;
      setTranslateX(next);
    }
  };

  const handleEnd = () => {
    const drag = dragRef.current;
    setAnimate(true);
    if (drag && drag.axis === 'h') {
      const shouldOpen = translateX < -SWIPE_REVEAL_PX / 2;
      isOpenRef.current = shouldOpen;
      setTranslateX(shouldOpen ? -SWIPE_REVEAL_PX : 0);
    }
    dragRef.current = null;
  };

  const handleDelete = () => {
    isOpenRef.current = false;
    setTranslateX(0);
    onDelete();
  };

  return (
    <div className='relative overflow-hidden rounded-lg' style={{ touchAction: 'pan-y' }}>
      {canDelete && translateX < 0 && (
        <button
          type='button'
          onClick={handleDelete}
          className='absolute inset-y-0 right-0 flex items-center justify-center bg-red-500 text-white'
          style={{ width: SWIPE_REVEAL_PX }}
          aria-label='Eliminar serie'
        >
          <Trash2 className='w-4 h-4' />
        </button>
      )}
      <div
        onTouchStart={(e) => {
          const t = e.touches[0];
          handleStart(t.clientX, t.clientY);
        }}
        onTouchMove={(e) => {
          const t = e.touches[0];
          handleMove(t.clientX, t.clientY);
        }}
        onTouchEnd={handleEnd}
        onTouchCancel={handleEnd}
        style={{
          transform: `translateX(${translateX}px)`,
          transition: animate ? 'transform 0.2s ease' : 'none',
        }}
        className={`relative ${cardBg}`}
      >
        {children}
      </div>
    </div>
  );
}

export function WorkoutTab({ setActiveTab }: WorkoutTabProps) {
  const { darkMode, theme } = useTheme();
  const { showConfirm } = useNotification();
  const {
    routines,
    selectedRoutine,
    setSelectedRoutine,
    confirmedSets,
    activeExerciseIndex,
    setActiveExerciseIndex,
    saveState,
    updateWorkoutSet,
    addWorkoutSet,
    removeWorkoutSet,
    saveWorkout,
    workouts,
    deleteWorkout,
    editWorkout,
    startWorkout,
    getActivePlan,
    getActivePlanContext,
    toggleConfirmedSet,
    cancelWorkout,
  } = useData();
  const [isRoutineDropdownOpen, setIsRoutineDropdownOpen] = useState(false);
  const [selectedRoutineOption, setSelectedRoutineOption] = useState<
    typeof routines[number] | null
  >(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date | null>(null);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [selectedWorkoutDetail, setSelectedWorkoutDetail] = useState<string | null>(null);
  const [showActivity, setShowActivity] = useState(false);
  const [sessionSummary, setSessionSummary] = useState<SessionSummary | null>(null);
  const pendingSummaryRef = useRef<SessionSummary | null>(null);
  const [liveMessage, setLiveMessage] = useState('');

  // Rest timer state
  const [restTimer, setRestTimer] = useState<RestTimerState | null>(null);
  const [restMinimized, setRestMinimized] = useState(false);
  const restIntervalRef = useRef<number | null>(null);
  const restClearTimeoutRef = useRef<number | null>(null);
  const restCompletionVibrationAtRef = useRef(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const initialProgressSignatureRef = useRef<string | null>(null);

  const buildProgressSignature = useCallback((routine: any, sets: Set<string>) => {
    const exercises = routine.exercises.map((exercise: any) =>
      exercise.sets.map((set: any) => ({
        weight: set.weight ?? 0,
        reps: set.reps ?? 0,
      }))
    );
    const confirmed = Array.from(sets).sort();
    return JSON.stringify({ exercises, confirmed });
  }, []);

  const formatTime = useCallback((seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }, []);

  const calculateSecondsLeft = useCallback((endAt: number) => {
    return Math.max(0, Math.ceil((endAt - Date.now()) / 1000));
  }, []);

  const triggerRestCompletionFeedback = useCallback(() => {
    const now = Date.now();
    if (now - restCompletionVibrationAtRef.current < 1200) return;
    restCompletionVibrationAtRef.current = now;
    navigator.vibrate?.([280, 140, 280]);

    try {
      const ctx = audioContextRef.current;
      if (!ctx) return;
      const beep = () => {
        const oscillator = ctx.createOscillator();
        const gain = ctx.createGain();
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(920, ctx.currentTime);
        gain.gain.setValueAtTime(0.0001, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.08, ctx.currentTime + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
        oscillator.connect(gain);
        gain.connect(ctx.destination);
        oscillator.start();
        oscillator.stop(ctx.currentTime + 0.18);
      };

      if (ctx.state === 'running') {
        beep();
      } else {
        ctx.resume().then(beep).catch(() => {
          // Ignore audio resume failures due to platform restrictions.
        });
      }
    } catch {
      // Ignore audio errors due to browser/platform restrictions.
    }
  }, []);

  const initRestAudio = useCallback(() => {
    if (audioContextRef.current || typeof window === 'undefined') return;
    const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextCtor) return;
    try {
      audioContextRef.current = new AudioContextCtor();
      audioContextRef.current.resume().catch(() => {
        // Resume may fail if browser blocks autoplay; we'll retry on next interaction.
      });
    } catch {
      // Ignore unsupported audio context creation errors.
    }
  }, []);

  const stopTimer = useCallback(() => {
    if (restIntervalRef.current !== null) {
      clearInterval(restIntervalRef.current);
      restIntervalRef.current = null;
    }
    if (restClearTimeoutRef.current !== null) {
      clearTimeout(restClearTimeoutRef.current);
      restClearTimeoutRef.current = null;
    }
    try {
      localStorage.removeItem(REST_TIMER_STORAGE_KEY);
    } catch {
      // Ignore storage failures on restrictive browsers/modes
    }
    restCompletionVibrationAtRef.current = 0;
    setRestTimer(null);
  }, []);

  const createTimer = useCallback((totalSeconds: number): RestTimerState => {
    const endAt = Date.now() + totalSeconds * 1000;
    return {
      total: totalSeconds,
      endAt,
      left: totalSeconds,
    };
  }, []);

  React.useEffect(() => {
    try {
      const rawTimer = localStorage.getItem(REST_TIMER_STORAGE_KEY);
      if (!rawTimer) return;

      const parsed = JSON.parse(rawTimer) as { total?: number; endAt?: number };
      if (!parsed || typeof parsed.total !== 'number' || typeof parsed.endAt !== 'number') {
        localStorage.removeItem(REST_TIMER_STORAGE_KEY);
        return;
      }

      const left = calculateSecondsLeft(parsed.endAt);
      if (left <= 0) {
        localStorage.removeItem(REST_TIMER_STORAGE_KEY);
        return;
      }

      setRestTimer({
        total: parsed.total,
        endAt: parsed.endAt,
        left,
      });
    } catch {
      // Ignore malformed storage data
    }
  }, [calculateSecondsLeft]);

  React.useEffect(() => {
    if (!restTimer || restTimer.left <= 0) {
      try {
        localStorage.removeItem(REST_TIMER_STORAGE_KEY);
      } catch {
        // Ignore storage failures on restrictive browsers/modes
      }
      return;
    }

    try {
      localStorage.setItem(
        REST_TIMER_STORAGE_KEY,
        JSON.stringify({ total: restTimer.total, endAt: restTimer.endAt })
      );
    } catch {
      // Ignore storage failures on restrictive browsers/modes
    }
  }, [restTimer]);

  React.useEffect(() => {
    const syncRestTimer = () => {
      setRestTimer((prev) => {
        if (!prev) return prev;
        const left = calculateSecondsLeft(prev.endAt);
        if (left === prev.left) return prev;
        if (left === 0 && prev.left > 0) {
          triggerRestCompletionFeedback();
        }
        return { ...prev, left };
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncRestTimer();
      }
    };

    window.addEventListener('focus', syncRestTimer);
    window.addEventListener('pageshow', syncRestTimer);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', syncRestTimer);
      window.removeEventListener('pageshow', syncRestTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [calculateSecondsLeft, triggerRestCompletionFeedback]);

  // Rest timer countdown effect
  useEffect(() => {
    if (restTimer === null || restTimer.left <= 0) {
      if (restIntervalRef.current !== null) {
        clearInterval(restIntervalRef.current);
        restIntervalRef.current = null;
      }
      return;
    }

    restIntervalRef.current = window.setInterval(() => {
      setRestTimer((prev) => {
        if (!prev || prev.left <= 1) {
          if (restIntervalRef.current !== null) {
            clearInterval(restIntervalRef.current);
            restIntervalRef.current = null;
          }
          if (prev && prev.left > 0) {
            triggerRestCompletionFeedback();
          }
          return prev ? { ...prev, left: 0 } : null;
        }
        const left = calculateSecondsLeft(prev.endAt);
        if (left <= 0) {
          triggerRestCompletionFeedback();
          return { ...prev, left: 0 };
        }
        if (left === prev.left) return prev;
        return { ...prev, left };
      });
    }, 1000);

    return () => {
      if (restIntervalRef.current !== null) {
        clearInterval(restIntervalRef.current);
        restIntervalRef.current = null;
      }
    };
  }, [calculateSecondsLeft, triggerRestCompletionFeedback, restTimer?.endAt, restTimer === null]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-reset after timer completes
  useEffect(() => {
    if (restTimer && restTimer.left === 0) {
      restClearTimeoutRef.current = window.setTimeout(() => {
        setRestTimer(null);
      }, 3000);
      return () => {
        if (restClearTimeoutRef.current !== null) {
          clearTimeout(restClearTimeoutRef.current);
          restClearTimeoutRef.current = null;
        }
      };
    }
  }, [restTimer?.left === 0]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {
          // Ignore close failures.
        });
        audioContextRef.current = null;
      }
    };
  }, []);

  // Cleanup timer when modal closes
  useEffect(() => {
    if (!selectedRoutine) {
      stopTimer();
    }
  }, [selectedRoutine, stopTimer]);

  // Calendar helper functions
  const getCalendarDays = (date: Date): CalendarDay[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    let startDayOfWeek = firstDay.getDay();
    startDayOfWeek = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

    const days: CalendarDay[] = [];

    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const prevDate = new Date(year, month, -i);
      days.push({ date: prevDate, isCurrentMonth: false });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push({ date: new Date(year, month, day), isCurrentMonth: true });
    }

    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }

    return days;
  };

  const getWorkoutsForDate = (date: Date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return workouts.filter((w: Workout) => w.date === dateStr);
  };

  const hasWorkoutOnDate = (date: Date) => {
    return getWorkoutsForDate(date).length > 0;
  };

  const hasPlanWorkoutOnDate = (date: Date) => {
    return getWorkoutsForDate(date).some((w: Workout) => !!w.training_plan_id);
  };

  const sortedWorkouts = useMemo(() => {
    return [...workouts].sort(
      (a, b) => parseLocalDate(b.date).getTime() - parseLocalDate(a.date).getTime()
    );
  }, [workouts]);

  const sortedRoutines = useMemo(() => {
    return [...routines].sort((a, b) =>
      a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })
    );
  }, [routines]);

  const activePlan = getActivePlan();

  const activePlanWeek = useMemo(() => {
    if (!activePlan?.start_date) return 1;
    const rawWeek = getTrainingPlanWeekNumber(parseLocalDate(activePlan.start_date));
    return Math.min(rawWeek, activePlan.duration_weeks);
  }, [activePlan]);

  const activePlanDayStatus = useMemo(() => {
    if (!activePlan) return [];

    const sortedDays = activePlan.days.slice().sort((a, b) => a.day_number - b.day_number);

    if (!activePlan.start_date || !activePlan.id) {
      return sortedDays.map((day) => ({
        day,
        completed: false,
      }));
    }

    const startDate = parseLocalDate(activePlan.start_date);
    const displayedWeekStart = getWeekStartDate(startDate);
    displayedWeekStart.setDate(displayedWeekStart.getDate() + (activePlanWeek - 1) * 7);
    const weekStart = displayedWeekStart < startDate ? startDate : displayedWeekStart;
    const weekEnd = getWeekEndDate(displayedWeekStart);

    const workoutsThisWeek = workouts.filter((workout) => {
      if (workout.training_plan_id !== activePlan.id) return false;
      const workoutDate = parseLocalDate(workout.date);
      return workoutDate >= weekStart && workoutDate <= weekEnd;
    });

    const completedByRoutine = new Map<string, number>();
    workoutsThisWeek.forEach((workout) => {
      const routineKey = workout.routine_id ? String(workout.routine_id) : '';
      if (!routineKey) return;
      completedByRoutine.set(routineKey, (completedByRoutine.get(routineKey) ?? 0) + 1);
    });

    return sortedDays.map((day) => {
      const routineKey = String(day.routine_id);
      const remainingCount = completedByRoutine.get(routineKey) ?? 0;
      const completed = remainingCount > 0;
      if (completed) {
        completedByRoutine.set(routineKey, remainingCount - 1);
      }
      return { day, completed };
    });
  }, [activePlan, activePlanWeek, workouts]);

  const todayPlanTarget = useMemo(() => {
    if (!activePlan) return null;

    const nextPendingDay = activePlanDayStatus.find((entry) => !entry.completed)?.day;
    const fallbackDay = activePlan.days.slice().sort((a, b) => a.day_number - b.day_number)[0];
    const targetDay = nextPendingDay ?? fallbackDay;

    if (!targetDay) return null;

    const routine = sortedRoutines.find((item) => item.id === targetDay.routine_id);
    return {
      day: targetDay,
      routine: routine ?? null,
      isCompleted: !nextPendingDay,
    };
  }, [activePlan, activePlanDayStatus, sortedRoutines]);

  const recentWorkouts = showAllHistory ? sortedWorkouts : sortedWorkouts.slice(0, 3);

  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const navigateMonth = (direction: number) => {
    setCalendarMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
    setSelectedCalendarDate(null);
  };

  const isEditing = selectedRoutine?.isEditing;
  const fromCalendar = selectedRoutine?.fromCalendar;
  const isDemo = selectedRoutine?.isDemo;
  const hasWorkoutProgressChanges = useMemo(() => {
    if (!selectedRoutine || isDemo) return false;
    if (!initialProgressSignatureRef.current) return false;
    return buildProgressSignature(selectedRoutine, confirmedSets) !== initialProgressSignatureRef.current;
  }, [selectedRoutine, isDemo, confirmedSets, buildProgressSignature]);

  React.useEffect(() => {
    if (!selectedRoutine) {
      initialProgressSignatureRef.current = null;
      return;
    }
    if (initialProgressSignatureRef.current === null) {
      initialProgressSignatureRef.current = buildProgressSignature(selectedRoutine, confirmedSets);
    }
  }, [selectedRoutine, confirmedSets, buildProgressSignature]);

  const handleClose = React.useCallback(() => {
    const closeWorkout = () => {
      pendingSummaryRef.current = null;
      cancelWorkout();
      if (fromCalendar) setActiveTab('progress');
    };

    if (!hasWorkoutProgressChanges) {
      closeWorkout();
      return;
    }

    showConfirm(
      'Se perderán los cambios. ¿Salir del entrenamiento?',
      closeWorkout
    );
  }, [fromCalendar, setActiveTab, cancelWorkout, hasWorkoutProgressChanges, showConfirm]);

  React.useEffect(() => {
    if (!selectedRoutine) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName)) {
        (target as HTMLInputElement).blur();
        return;
      }
      if (restTimer && restTimer.left > 0 && !restMinimized) {
        setRestMinimized(true);
        return;
      }
      if (restTimer && restTimer.left === 0) {
        stopTimer();
        return;
      }
      handleClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedRoutine, handleClose, restTimer, restMinimized, stopTimer]);

  const workoutDialogRef = React.useRef<HTMLElement>(null);
  const isWorkoutOpen = Boolean(selectedRoutine);

  React.useEffect(() => {
    if (!isWorkoutOpen) return;
    const section = workoutDialogRef.current;
    if (!section) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    window.scrollTo({ top: 0 });
    section.focus({ preventScroll: true });

    return () => {
      previouslyFocused?.focus?.();
    };
  }, [isWorkoutOpen]);

  const buildSessionSummary = React.useCallback((): SessionSummary | null => {
    if (!selectedRoutine) return null;
    let totalVolume = 0;
    let completed = 0;
    let total = 0;
    const prsAchieved: { exercise: string; weight: number }[] = [];
    selectedRoutine.exercises.forEach((exercise, exerciseIndex) => {
      const prs = getExercisePRs(exercise.name, workouts, selectedRoutine.date);
      let bestNewWeight = 0;
      exercise.sets.forEach((set, setIndex) => {
        total++;
        if (confirmedSets.has(`${exerciseIndex}-${setIndex}`) && (set.reps ?? 0) > 0) {
          completed++;
          const weight = set.weight || 0;
          totalVolume += weight * (set.reps || 0);
          if (prs.hasHistory && weight > prs.maxWeight && weight > bestNewWeight) {
            bestNewWeight = weight;
          }
        }
      });
      if (bestNewWeight > 0) {
        prsAchieved.push({ exercise: exercise.name, weight: bestNewWeight });
      }
    });
    const previous = workouts
      .filter((w) => w.routine_name === selectedRoutine.name && w.date < selectedRoutine.date)
      .sort((a, b) => (a.date < b.date ? 1 : -1))[0];
    const previousVolume = previous ? getWorkoutTotalVolume(previous) : null;
    const volumeDelta =
      previousVolume && previousVolume > 0
        ? Math.round(((totalVolume - previousVolume) / previousVolume) * 100)
        : null;
    return {
      routineName: selectedRoutine.name,
      totalVolume,
      completed,
      total,
      prsAchieved,
      volumeDelta,
    };
  }, [selectedRoutine, workouts, confirmedSets]);

  React.useEffect(() => {
    if (!isWorkoutOpen && pendingSummaryRef.current) {
      setSessionSummary(pendingSummaryRef.current);
      pendingSummaryRef.current = null;
      navigator.vibrate?.([120, 60, 120]);
    }
  }, [isWorkoutOpen]);

  React.useEffect(() => {
    if (!selectedRoutine) return;
    if (activeExerciseIndex >= selectedRoutine.exercises.length) {
      setActiveExerciseIndex(
        Math.max(0, selectedRoutine.exercises.length - 1)
      );
    }
  }, [activeExerciseIndex, selectedRoutine?.exercises.length, selectedRoutine, setActiveExerciseIndex]);

  if (!selectedRoutine) {
    const completedDays = activePlanDayStatus.filter((entry) => entry.completed).length;
    const totalPlanDays = activePlanDayStatus.length;

    return (
      <div className='space-y-4 sm:space-y-6'>
        {sessionSummary && (
          <div
            className='fixed inset-0 z-[110] overflow-y-auto bg-gradient-to-b from-[#b4532a] via-[#a34a24] to-[#6f2810] text-white'
            role='dialog'
            aria-modal='true'
            aria-label='Resumen de la sesión'
            onKeyDown={(e) => e.key === 'Escape' && setSessionSummary(null)}
          >
            <div className='min-h-full flex flex-col items-center justify-center gap-5 px-6 py-[max(2rem,env(safe-area-inset-top))] text-center animate-rise-in'>
              <p className='text-sm font-semibold uppercase tracking-[0.2em] text-orange-100'>
                Entrenamiento terminado
              </p>
              <h2 className='text-3xl sm:text-4xl font-black tracking-[-0.02em]' style={{ textWrap: 'balance' }}>
                {sessionSummary.routineName}
              </h2>
              <div>
                <p className='font-mono font-bold leading-none text-6xl sm:text-7xl'>
                  {Math.round(sessionSummary.totalVolume)}
                </p>
                <p className='mt-2 text-sm text-orange-100'>kg de volumen</p>
              </div>
              <p className='text-base font-semibold'>
                {sessionSummary.completed}/{sessionSummary.total} series
              </p>
              {sessionSummary.volumeDelta !== null && (
                <p className='text-sm text-orange-100'>
                  {sessionSummary.volumeDelta >= 0 ? '▲' : '▼'} {Math.abs(sessionSummary.volumeDelta)}% vs última sesión
                </p>
              )}
              {sessionSummary.prsAchieved.length > 0 && (
                <div className='flex flex-col items-center gap-1.5'>
                  {sessionSummary.prsAchieved.map((pr) => (
                    <p
                      key={pr.exercise}
                      className='inline-flex items-center gap-2 text-sm font-semibold bg-white/15 rounded-full px-4 py-1.5'
                    >
                      <Trophy className='w-4 h-4 text-yellow-300' />
                      PR: {pr.exercise} — {pr.weight} kg
                    </p>
                  ))}
                </div>
              )}
              <button
                type='button'
                autoFocus
                onClick={() => setSessionSummary(null)}
                className='pressable mt-2 px-10 py-3 rounded-2xl bg-white/15 hover:bg-white/25 font-semibold'
              >
                Listo
              </button>
            </div>
          </div>
        )}
        {activePlan && (
          <div className={`p-4 sm:p-6 rounded-[30px] ${theme.card} border ${theme.border}`}>
            <div className='flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
              <div className='space-y-2'>
                <div className={`flex items-center gap-2 ${darkMode ? 'text-[#e89b6e]' : 'text-[#8e3f1d]'}`}>
                  <Target className='w-4 h-4' />
                  <span className='text-xs font-semibold uppercase tracking-[0.18em]'>Hoy</span>
                </div>
                <div>
                  <h2 className='text-xl sm:text-2xl font-bold'>{activePlan.name}</h2>
                  <p className={`text-sm ${theme.textSecondary}`}>
                    Semana {activePlanWeek}/{activePlan.duration_weeks} · {completedDays}/{totalPlanDays} días
                  </p>
                </div>
                {todayPlanTarget?.day && (
                  <div className={`rounded-lg border ${theme.border} px-4 py-3`}>
                    <p className='text-base font-semibold'>
                      Día {todayPlanTarget.day.day_number}
                      {todayPlanTarget.day.label ? ` · ${todayPlanTarget.day.label}` : ''}
                    </p>
                    <p className={`text-sm ${theme.textSecondary}`}>
                      {todayPlanTarget.routine
                        ? todayPlanTarget.routine.name
                        : 'Rutina no disponible'}
                    </p>
                    {todayPlanTarget.isCompleted && (
                      <p className={`mt-2 text-xs ${darkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
                        Semana completa
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className='flex flex-col gap-2 lg:min-w-72'>
                <button
                  type='button'
                  onClick={() => {
                    if (!todayPlanTarget?.routine) return;
                    const planContext =
                      activePlan?.id && todayPlanTarget.day
                        ? {
                            planId: activePlan.id,
                            planName: activePlan.name,
                            weekNumber: activePlanWeek,
                            totalWeeks: activePlan.duration_weeks,
                            dayNumber: todayPlanTarget.day.day_number,
                            dayLabel: todayPlanTarget.day.label,
                          }
                        : null;
                    startWorkout(todayPlanTarget.routine, planContext);
                  }}
                  disabled={!todayPlanTarget?.routine}
                  className={`w-full px-4 py-3 rounded-lg font-medium text-sm flex items-center justify-center gap-2 ${
                    todayPlanTarget?.routine
                      ? `pressable text-white hover:brightness-105 bg-gradient-to-r ${darkMode ? 'from-[#d97745] to-[#bc5f31]' : 'from-[#b4532a] to-[#d97745]'}`
                      : 'bg-stone-500 text-white opacity-60 cursor-not-allowed'
                  }`}
                >
                  <PlayCircle className='w-4 h-4' />
                  {todayPlanTarget?.day ? `Entrenar día ${todayPlanTarget.day.day_number}` : 'Entrenar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Routine picker */}
        <div
          className={`p-4 sm:p-6 rounded-[24px] ${theme.card} border ${theme.border} text-center`}
        >
          {sortedRoutines.length === 0 ? (
            <div className='space-y-3'>
              <p className={`text-xs sm:text-sm ${theme.textSecondary}`}>
                Sin rutinas
              </p>
              <button
                type='button'
                onClick={() => setActiveTab('routines')}
                className={`pressable px-5 py-2.5 rounded-lg text-sm font-medium text-white hover:brightness-105 bg-gradient-to-r ${darkMode ? 'from-[#d97745] to-[#bc5f31]' : 'from-[#b4532a] to-[#d97745]'}`}
              >
                Crear rutina
              </button>
            </div>
          ) : (
            <div className='space-y-2 sm:space-y-3 text-left'>
              <div className='flex flex-col sm:flex-row gap-2 sm:gap-3'>
                <div className='relative w-full'>
                  <button
                    type='button'
                    onClick={() => setIsRoutineDropdownOpen((prev) => !prev)}
                    aria-expanded={isRoutineDropdownOpen}
                    aria-haspopup='listbox'
                    className={`w-full px-4 py-3 sm:py-2 rounded-lg border ${theme.input} text-left text-base sm:text-sm ${theme.hover} flex items-center justify-between gap-3`}
                  >
                    <span className='truncate'>
                      {selectedRoutineOption
                        ? selectedRoutineOption.name
                        : activePlan ? 'Otra rutina' : 'Elegir rutina'}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 flex-shrink-0 transition-transform ${
                        isRoutineDropdownOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {isRoutineDropdownOpen && (
                    <div
                      role='listbox'
                      aria-label='Rutinas disponibles'
                      className={`absolute z-20 mt-2 w-full rounded-lg border ${theme.border} ${theme.card} shadow-lg max-h-64 overflow-auto`}
                    >
                      {sortedRoutines.map((routine, index) => {
                        const routineId =
                          routine.id ?? routine.name ?? String(index);
                        return (
                          <button
                            key={routineId}
                            type='button'
                            role='option'
                            aria-selected={
                              selectedRoutineOption?.id === routine.id &&
                              selectedRoutineOption?.name === routine.name
                            }
                            onClick={() => {
                              setSelectedRoutineOption(routine);
                              setIsRoutineDropdownOpen(false);
                            }}
                            className={`w-full text-left px-4 py-3 text-sm ${theme.hover} ${
                              selectedRoutineOption?.id === routine.id &&
                              selectedRoutineOption?.name === routine.name
                                ? 'bg-[#b4532a] text-white'
                                : ''
                            }`}
                          >
                            <div className='flex items-center justify-between gap-3'>
                              <span className='truncate'>{routine.name}</span>
                              <span className='text-xs flex-shrink-0'>
                                {routine.exercises.length} ejercicios
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <button
                  type='button'
                  onClick={() => {
                    if (!selectedRoutineOption) return;
                    const planContext = selectedRoutineOption.id
                      ? getActivePlanContext(selectedRoutineOption.id)
                      : null;
                    startWorkout(selectedRoutineOption, planContext);
                  }}
                  disabled={!selectedRoutineOption}
                  className={`w-full sm:w-auto px-4 py-3 sm:py-2 rounded-lg font-medium text-base sm:text-sm ${
                    selectedRoutineOption
                      ? `pressable text-white hover:brightness-105 bg-gradient-to-r ${darkMode ? 'from-[#d97745] to-[#bc5f31]' : 'from-[#b4532a] to-[#d97745]'}`
                      : 'bg-stone-500 text-white opacity-60 cursor-not-allowed'
                  }`}
                >
                  Entrenar
                </button>
              </div>
            </div>
          )}
        </div>

        <div className={`rounded-[24px] ${theme.card} border ${theme.border}`}>
          <button
            type='button'
            onClick={() => setShowActivity((prev) => !prev)}
            aria-expanded={showActivity}
            className={`w-full flex items-center justify-between gap-3 px-4 py-3.5 sm:px-6 rounded-[24px] ${theme.hover}`}
          >
            <span className='text-sm sm:text-base font-bold'>Historial</span>
            <span className={`flex items-center gap-2 text-xs sm:text-sm ${theme.textSecondary}`}>
              {sortedWorkouts.length === 1
                ? '1 entrenamiento'
                : `${sortedWorkouts.length} entrenamientos`}
              <ChevronDown
                className={`w-4 h-4 transition-transform ${showActivity ? 'rotate-180' : ''}`}
              />
            </span>
          </button>
          {showActivity && (
          <div className='grid gap-4 sm:gap-6 px-3 pb-3 sm:px-4 sm:pb-4 lg:grid-cols-2 lg:items-stretch'>
          {/* Calendar */}
          <div
            className={`p-3 sm:p-4 rounded-lg ${theme.card} border ${theme.border} lg:h-[36rem] lg:flex lg:flex-col`}
          >
            <div className='flex items-center justify-between mb-2'>
              <button
                onClick={() => navigateMonth(-1)}
                className={`p-3 -m-1.5 rounded-lg ${theme.hover} touch-manipulation`}
                aria-label='Mes anterior'
              >
                <ChevronLeft className='w-5 h-5' />
              </button>
              <h3 className='text-sm font-semibold capitalize'>
                {calendarMonth.toLocaleDateString('es-ES', {
                  month: 'short',
                  year: 'numeric',
                })}
              </h3>
              <button
                onClick={() => navigateMonth(1)}
                className={`p-3 -m-1.5 rounded-lg ${theme.hover} touch-manipulation`}
                aria-label='Mes siguiente'
              >
                <ChevronRight className='w-5 h-5' />
              </button>
            </div>

            <div className='grid grid-cols-7 gap-1 mb-1'>
              {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((day) => (
                <div
                  key={day}
                  className={`text-center text-[10px] font-medium ${theme.textSecondary}`}
                >
                  {day}
                </div>
              ))}
            </div>

            <div className='grid grid-cols-7 gap-1'>
              {getCalendarDays(calendarMonth).map((dayObj, index) => {
                const hasWorkout = hasWorkoutOnDate(dayObj.date);
                const hasPlanWorkout = hasPlanWorkoutOnDate(dayObj.date);
                const isTodayDate = isToday(dayObj.date);
                const isSelected =
                  selectedCalendarDate &&
                  dayObj.date.toDateString() === selectedCalendarDate.toDateString();

                return (
                  <button
                    key={index}
                    onClick={() => hasWorkout && setSelectedCalendarDate(dayObj.date)}
                    disabled={!hasWorkout}
                    aria-label={
                      hasWorkout
                        ? `Día ${dayObj.date.getDate()}: ${hasPlanWorkout ? 'entrenamiento del plan' : 'entrenamiento libre'}`
                        : undefined
                    }
                    className={`
                      w-full max-w-11 mx-auto aspect-square flex items-center justify-center rounded-full text-xs
                      ${!dayObj.isCurrentMonth ? 'opacity-20' : ''}
                      ${hasWorkout ? (hasPlanWorkout ? 'bg-emerald-700 font-bold underline underline-offset-[3px] text-white cursor-pointer hover:bg-emerald-800' : 'bg-[#b4532a] text-white cursor-pointer hover:bg-[#9a4523]') : ''}
                      ${isTodayDate && !hasWorkout ? 'ring-1 ring-[#b4532a]' : ''}
                      ${isSelected ? 'ring-2 ring-[#b4532a]' : ''}
                      ${!hasWorkout ? 'cursor-default' : ''}
                    `}
                  >
                    {dayObj.date.getDate()}
                  </button>
                );
              })}
            </div>

            {selectedCalendarDate && (
              <div
                className={`mt-3 pt-3 border-t ${theme.border} lg:flex lg:flex-col lg:flex-1 lg:min-h-0`}
              >
                <div className='flex items-center justify-between mb-2'>
                  <span className='text-xs font-medium'>
                    {selectedCalendarDate.toLocaleDateString('es-ES', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short',
                    })}
                  </span>
                  <button
                    onClick={() => setSelectedCalendarDate(null)}
                    className={`p-2 -m-1 rounded ${theme.hover}`}
                    aria-label='Cerrar'
                  >
                    <X className='w-4 h-4' />
                  </button>
                </div>
                <div className='space-y-2 lg:min-h-0 lg:overflow-y-auto lg:pr-1'>
                  {getWorkoutsForDate(selectedCalendarDate).map((workout: Workout) => (
                    <div
                      key={workout.id}
                      className={`p-2 rounded border ${theme.border} text-xs`}
                    >
                      <div className='flex items-center justify-between mb-1'>
                        <div className='flex items-center gap-2 min-w-0'>
                          <p className='font-medium truncate'>{workout.routine_name}</p>
                          {workout.training_plan_id && (
                            <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold ${darkMode ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-600/15 text-emerald-900'}`}>
                              Plan
                            </span>
                          )}
                          {workout.syncStatus === 'pending' && (
                            <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold ${darkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-500/25 text-amber-900'}`}>
                              Pendiente
                            </span>
                          )}
                          {workout.syncStatus === 'failed' && (
                            <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold ${darkMode ? 'bg-red-500/20 text-red-300' : 'bg-red-500/15 text-red-800'}`}>
                              Fallo sync
                            </span>
                          )}
                        </div>
                        <div className='flex gap-1'>
                          <button
                            onClick={(e) => {
                              if (workout.syncStatus) return;
                              editWorkout(workout, e, false);
                              setSelectedCalendarDate(null);
                            }}
                            disabled={Boolean(workout.syncStatus)}
                            className={`p-2 rounded ${theme.hover}`}
                            aria-label='Editar'
                          >
                            <Edit2 className='w-4 h-4' />
                          </button>
                          <button
                            onClick={(e) => {
                              if (workout.syncStatus) return;
                              deleteWorkout(workout.id!, e);
                              setSelectedCalendarDate(null);
                            }}
                            disabled={Boolean(workout.syncStatus)}
                            className='p-2 rounded hover:bg-red-600 text-red-500 hover:text-white disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-red-500'
                            aria-label='Eliminar'
                          >
                            <Trash2 className='w-4 h-4' />
                          </button>
                        </div>
                      </div>
                      {workout.exercises.map((ex, idx) => (
                        <div
                          key={idx}
                          className={`${theme.textSecondary} flex justify-between`}
                        >
                          <span className='truncate mr-2'>{ex.name}</span>
                          <span className='flex-shrink-0'>{ex.sets.length}x</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Workout History */}
          <div
            className={`p-4 sm:p-6 rounded-lg ${theme.card} border ${theme.border} lg:h-[36rem] lg:flex lg:flex-col`}
          >
            <div className='flex items-center justify-between mb-3 sm:mb-4'>
              <h3 className='text-lg sm:text-xl font-bold'>Recientes</h3>
              {sortedWorkouts.length > 3 && (
                <button
                  onClick={() => setShowAllHistory((prev) => !prev)}
                  className={`text-xs sm:text-sm font-semibold ${darkMode ? 'text-[#e89b6e] hover:text-[#f2c7ae]' : 'text-[#8e3f1d] hover:text-[#6f2810]'}`}
                >
                  {showAllHistory ? 'Ver menos' : 'Ver más'}
                </button>
              )}
            </div>
            {recentWorkouts.length === 0 ? (
              <div className='text-center py-6 lg:flex lg:flex-1 lg:items-center lg:justify-center lg:flex-col'>
                <Calendar className='w-12 h-12 mx-auto mb-3 opacity-50' />
                <p className={`${theme.textSecondary}`}>
                  Sin entrenamientos
                </p>
              </div>
            ) : (
              <div className='space-y-2 sm:space-y-3 lg:flex-1 lg:min-h-0 lg:overflow-y-auto lg:pr-1'>
                {recentWorkouts.map((workout: Workout) => {
                  const totalVolume = getWorkoutTotalVolume(workout);
                  const isExpanded = selectedWorkoutDetail === workout.id;

                  return (
                    <div key={workout.id}>
                      <div
                        onClick={() =>
                          setSelectedWorkoutDetail(isExpanded ? null : workout.id!)
                        }
                        className={`p-3 sm:p-4 rounded-lg border ${theme.border} cursor-pointer transition-colors ${theme.hover} ${
                          isExpanded ? 'ring-2 ring-[#b4532a]' : ''
                        }`}
                      >
                        <div className='flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-2 mb-1 sm:mb-2'>
                          <div className='flex items-center gap-2 min-w-0'>
                            <span className='font-medium text-sm sm:text-base truncate'>
                              {workout.routine_name}
                            </span>
                            {workout.training_plan_id && (
                              <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold ${darkMode ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-600/15 text-emerald-900'}`}>
                                Plan
                              </span>
                            )}
                            {workout.syncStatus === 'pending' && (
                              <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold ${darkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-500/25 text-amber-900'}`}>
                                Pendiente
                              </span>
                            )}
                            {workout.syncStatus === 'failed' && (
                              <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold ${darkMode ? 'bg-red-500/20 text-red-300' : 'bg-red-500/15 text-red-800'}`}>
                                Fallo sync
                              </span>
                            )}
                          </div>
                          <span
                            className={`${theme.textSecondary} text-xs sm:text-sm flex-shrink-0`}
                          >
                            {parseLocalDate(workout.date).toLocaleDateString('es-ES')}
                          </span>
                        </div>
                        <div className='flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2'>
                          <p
                            className={`text-xs sm:text-sm ${theme.textSecondary}`}
                          >
                            {workout.exercises.length} ejercicios
                          </p>
                          <span className={`text-xs sm:text-sm font-semibold ${darkMode ? 'text-[#e89b6e]' : 'text-[#8e3f1d]'}`}>
                            vol. {Math.round(totalVolume)} kg
                          </span>
                        </div>
                      </div>

                      {isExpanded && (
                        <div
                          className={`mt-2 p-3 sm:p-4 rounded-lg border ${theme.border} ${theme.card} space-y-2`}
                        >
                          <div className='flex items-center justify-end mb-2'>
                            <div className='flex gap-1 sm:gap-2'>
                              <button
                                onClick={(e) => {
                                  if (workout.syncStatus) return;
                                  editWorkout(workout, e);
                                }}
                                disabled={Boolean(workout.syncStatus)}
                                className={`p-2 rounded-lg ${theme.hover} touch-manipulation disabled:opacity-40`}
                                aria-label='Editar entrenamiento'
                              >
                                <Edit2 className='w-4 h-4' />
                              </button>
                              <button
                                onClick={(e) => {
                                  if (workout.syncStatus) return;
                                  deleteWorkout(workout.id!, e);
                                }}
                                disabled={Boolean(workout.syncStatus)}
                                className='p-2 rounded-lg hover:bg-red-600 active:bg-red-700 text-red-500 hover:text-white touch-manipulation disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-red-500'
                                aria-label='Eliminar entrenamiento'
                              >
                                <Trash2 className='w-4 h-4' />
                              </button>
                            </div>
                          </div>
                          {getWorkoutExerciseSummary(workout).map((exercise, idx) => (
                            <div
                              key={idx}
                              className={`p-2 sm:p-3 rounded border ${theme.border} flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2 ${
                                !exercise.performed ? 'opacity-50' : ''
                              }`}
                            >
                              <span
                                className={`font-medium text-sm sm:text-base inline-flex items-center gap-1.5 ${
                                  !exercise.performed ? theme.textSecondary : ''
                                }`}
                              >
                                {exercise.name}
                                <ExerciseDemoButton name={exercise.name} iconClassName='w-4 h-4' />
                                {!exercise.performed && (
                                  <span className='ml-1 text-xs bg-stone-500 text-white px-2 py-0.5 rounded'>
                                    No realizado
                                  </span>
                                )}
                              </span>
                              {exercise.performed && (
                                <div className='flex flex-wrap gap-2 sm:gap-3 text-xs sm:text-sm'>
                                  <span className={theme.textSecondary}>
                                    {exercise.totalSets} series · {exercise.totalReps} reps · máx {exercise.maxWeight} kg
                                  </span>
                                  <span className={`font-semibold ${darkMode ? 'text-[#e89b6e]' : 'text-[#8e3f1d]'}`}>
                                    vol. {Math.round(exercise.totalVolume)} kg
                                  </span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
          )}
        </div>
      </div>
    );
  }
  const isSetComplete = (set: { weight: number; reps: number }, exerciseIndex: number, setIndex: number) =>
    (set.reps ?? 0) > 0 && confirmedSets.has(`${exerciseIndex}-${setIndex}`);

  const exerciseProgress = selectedRoutine.exercises.map((exercise, exerciseIndex) => {
    const totalSets = exercise.sets.length;
    const completedSets = exercise.sets.filter((set, setIndex) => isSetComplete(set, exerciseIndex, setIndex)).length;
    return {
      totalSets,
      completedSets,
      pendingSets: totalSets - completedSets,
      isComplete: totalSets > 0 && completedSets === totalSets,
    };
  });

  const sessionExerciseItems = selectedRoutine.exercises.map((exercise, index) => ({
    name: exercise.name,
    completedSets: exerciseProgress[index]?.completedSets ?? 0,
    totalSets: exerciseProgress[index]?.totalSets ?? 0,
    isComplete: exerciseProgress[index]?.isComplete ?? false,
  }));
  const pendingExerciseCount = sessionExerciseItems.filter(
    (exercise) => !exercise.isComplete
  ).length;

  const activeExercise = selectedRoutine.exercises[activeExerciseIndex];
  const activeProgress = exerciseProgress[activeExerciseIndex];
  const activePRs = activeExercise
    ? getExercisePRs(activeExercise.name, workouts, selectedRoutine.date)
    : null;
  const lastSessionSets = activeExercise && !isDemo
    ? getLastSessionSets(activeExercise.name, workouts, selectedRoutine.date)
    : null;

  const applyLastSessionSet = (setIndex: number, lastSet: LastSessionSet) => {
    updateWorkoutSet(activeExerciseIndex, setIndex, 'weight', String(lastSet.weight));
    updateWorkoutSet(activeExerciseIndex, setIndex, 'reps', String(lastSet.reps));
  };

  return (
    <>
      <section
        ref={workoutDialogRef}
        tabIndex={-1}
        aria-label={`Entrenamiento: ${selectedRoutine.name}`}
        className='outline-none max-w-3xl mx-auto'
      >
        <span className='sr-only' role='status'>{liveMessage}</span>
        <span className='sr-only' role='status'>
          {saveState === 'saving'
            ? 'Guardando entrenamiento'
            : saveState === 'saved'
            ? 'Entrenamiento terminado'
            : restTimer
            ? restTimer.left === 0
              ? 'Descanso terminado'
              : 'Descanso en curso'
            : ''}
        </span>
        <div className='space-y-3 sm:space-y-4 pb-4'>
          <div
            className={`p-4 sm:p-6 rounded-[24px] ${theme.card} border ${theme.border}`}
          >
            <div className='flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between'>
              <div className='min-w-0'>
                {selectedRoutine.planContext && (
                  <p className={`mb-1 text-xs font-medium ${darkMode ? 'text-[#e89b6e]' : 'text-[#8e3f1d]'}`}>
                    {selectedRoutine.planContext.planName} · Semana {selectedRoutine.planContext.weekNumber}/{selectedRoutine.planContext.totalWeeks} · Día {selectedRoutine.planContext.dayNumber}
                    {selectedRoutine.planContext.dayLabel ? ` (${selectedRoutine.planContext.dayLabel})` : ''}
                  </p>
                )}
                <h2 className='text-2xl sm:text-3xl font-black tracking-[-0.02em] leading-tight' style={{ textWrap: 'balance' }}>
                  {isEditing && <span className={darkMode ? 'text-[#e89b6e]' : 'text-[#8e3f1d]'}>Editando: </span>}
                  {isDemo && <span className={darkMode ? 'text-amber-400' : 'text-amber-700'}>Vista previa: </span>}
                  {selectedRoutine.name}
                </h2>
              </div>
              {!isDemo && (
                <label className='flex items-center gap-2 sm:shrink-0'>
                  <span className={`text-xs font-medium ${theme.textSecondary}`}>Fecha</span>
                  <input
                    type='date'
                    value={selectedRoutine.date}
                    onChange={(e) =>
                      setSelectedRoutine({
                        ...selectedRoutine,
                        date: e.target.value,
                      })
                    }
                    style={{ colorScheme: darkMode ? 'dark' : 'light' }}
                    className={`min-w-0 px-3 py-2 rounded-lg border appearance-none ${theme.input} text-base sm:text-sm`}
                  />
                </label>
              )}
            </div>
          </div>

          <SessionExerciseDropdown
            exercises={sessionExerciseItems}
            activeIndex={activeExerciseIndex}
            pendingCount={pendingExerciseCount}
            theme={theme}
            onSelect={(index) => {
              setActiveExerciseIndex(index);
              setLiveMessage(`Ejercicio actual: ${selectedRoutine.exercises[index].name}`);
            }}
          />

          {activeExercise && (
            <div
              className={`p-4 sm:p-6 rounded-[24px] ${theme.card} border ${theme.border}`}
            >
              <div className='flex items-start gap-2 mb-3 sm:mb-4'>
                <h3 className='text-2xl sm:text-3xl font-black tracking-[-0.02em] leading-tight' style={{ textWrap: 'balance' }}>
                  {activeExercise.name}
                </h3>
                <ExerciseDemoButton name={activeExercise.name} iconClassName='w-6 h-6' />
                <span className={`ml-auto pt-1.5 text-xs sm:text-sm tabular-nums flex-shrink-0 ${theme.textSecondary}`}>
                  {activeProgress?.completedSets ?? 0}/{activeProgress?.totalSets ?? 0}
                </span>
              </div>
              {/* Personal Records Section */}
              {activePRs?.hasHistory && (
                <div className={`mb-3 px-3 py-2 rounded-xl border ${theme.border} flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs sm:text-sm`}>
                  <span className='inline-flex items-center gap-1.5'>
                    <Trophy className='w-4 h-4 text-yellow-500' />
                    <span className={`${theme.textSecondary} font-medium`}>PR</span>
                    <span className='font-bold'>{activePRs.maxWeight} kg</span>
                  </span>
                  <span className={theme.textSecondary} aria-hidden='true'>·</span>
                  <span>
                    <span className={theme.textSecondary}>Volumen </span>
                    <span className='font-bold'>{Math.round(activePRs.maxVolume)} kg</span>
                  </span>
                  <span className={theme.textSecondary} aria-hidden='true'>·</span>
                  <span>
                    <span className={theme.textSecondary}>Prom </span>
                    <span className='font-bold'>{activePRs.avgReps} reps</span>
                  </span>
                </div>
              )}
              <div className='space-y-2'>
                {/* Column labels */}
                <div className='flex items-center gap-1 sm:gap-2 px-1'>
                  <span className='w-11 flex-shrink-0' />
                  <div className='flex items-center gap-1 sm:gap-2 flex-1 min-w-0'>
                    <div className='flex-1 min-w-0 text-center'>
                      <span className={`text-[11px] sm:text-xs font-semibold uppercase tracking-wider ${theme.textSecondary}`}>Peso (kg)</span>
                    </div>
                    <div className='flex-1 min-w-0 text-center'>
                      <span className={`text-[11px] sm:text-xs font-semibold uppercase tracking-wider ${theme.textSecondary}`}>Reps</span>
                    </div>
                  </div>
                  {!isDemo && activeExercise.sets.length > 1 && (
                    <span className='w-9 sm:w-11 flex-shrink-0' />
                  )}
                </div>
                {activeExercise.sets.map((set, setIndex) => {
                  const setKey = `${activeExerciseIndex}-${setIndex}`;
                  const isConfirmed = confirmedSets.has(setKey);
                  const canDelete = !isDemo && activeExercise.sets.length > 1;
                  const beatsPR = Boolean(
                    activePRs?.hasHistory && (set.weight || 0) > (activePRs?.maxWeight ?? 0)
                  );
                  const isPR = isConfirmed && beatsPR;
                  const lastSet = lastSessionSets
                    ? lastSessionSets[setIndex] ?? lastSessionSets[lastSessionSets.length - 1]
                    : null;
                  return (
                  <SwipeableSetRow
                    key={`${activeExerciseIndex}-${setIndex}`}
                    cardBg={theme.card}
                    canDelete={canDelete}
                    totalSets={activeExercise.sets.length}
                    onDelete={() => removeWorkoutSet(activeExerciseIndex, setIndex)}
                  >
                  <div
                    className={`flex flex-wrap items-center gap-1 sm:gap-2 px-1 py-1.5 sm:py-2 rounded-lg transition-colors ${
                      isPR
                        ? 'bg-[#d97745]/15'
                        : isConfirmed
                        ? 'bg-green-500/10'
                        : ''
                    }`}
                  >
                    {/* Checkbox */}
                    <button
                      type='button'
                      disabled={isDemo}
                      onClick={() => {
                        if (isDemo) return;
                        if (!isConfirmed && beatsPR) {
                          navigator.vibrate?.([90, 40, 90]);
                          setLiveMessage(`Nuevo PR: ${set.weight || 0} kilos en ${activeExercise.name}`);
                        }
                        toggleConfirmedSet(activeExerciseIndex, setIndex);
                      }}
                      className={`w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-full border-2 transition-colors touch-manipulation ${
                        isPR
                          ? 'bg-[#d97745] border-[#d97745] text-white animate-pr-pop'
                          : isConfirmed
                          ? 'bg-green-500 border-green-500 text-white'
                          : `border-stone-500 ${isDemo ? 'opacity-60 cursor-not-allowed' : ''}`
                      }`}
                      aria-label={
                        isPR
                          ? 'Serie con nuevo PR. Desmarcar serie'
                          : isConfirmed
                          ? 'Desmarcar serie'
                          : 'Marcar serie como completada'
                      }
                    >
                      {isConfirmed ? (
                        isPR ? (
                          <Trophy className='w-5 h-5' />
                        ) : (
                          <Check className='w-5 h-5' />
                        )
                      ) : (
                        <span className='text-sm font-semibold'>{setIndex + 1}</span>
                      )}
                    </button>
                    {/* Weight: -/input/+ */}
                    <div className='flex items-center gap-0.5 flex-[1.2] min-w-[8.25rem]'>
                      <button
                        type='button'
                        disabled={isDemo}
                        onClick={() => {
                          const newVal = Math.max(0, (set.weight || 0) - 2.5);
                          updateWorkoutSet(activeExerciseIndex, setIndex, 'weight', String(newVal));
                        }}
                        aria-label={`Restar 2,5 kilos a la serie ${setIndex + 1}`}
                        className={`w-10 h-11 sm:w-11 flex-shrink-0 flex items-center justify-center rounded-l-lg border ${theme.border} ${theme.hover} ${isDemo ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        <Minus className='w-4 h-4' />
                      </button>
                      <input
                        type='number'
                        step='0.5'
                        placeholder={lastSet ? String(lastSet.weight) : 'Kg'}
                        aria-label={`Peso de la serie ${setIndex + 1} en kilos`}
                        value={set.weight || ''}
                        onChange={(e) => {
                          updateWorkoutSet(activeExerciseIndex, setIndex, 'weight', e.target.value);
                        }}
                        min={0}
                        disabled={isDemo}
                        className={`flex-1 min-w-0 px-0.5 py-3 border-y ${theme.input} text-sm tabular-nums text-center ${isDemo ? 'opacity-60 cursor-not-allowed' : ''}`}
                      />
                      <button
                        type='button'
                        disabled={isDemo}
                        onClick={() => {
                          const newVal = (set.weight || 0) + 2.5;
                          updateWorkoutSet(activeExerciseIndex, setIndex, 'weight', String(newVal));
                        }}
                        aria-label={`Sumar 2,5 kilos a la serie ${setIndex + 1}`}
                        className={`w-10 h-11 sm:w-11 flex-shrink-0 flex items-center justify-center rounded-r-lg border ${theme.border} ${theme.hover} ${isDemo ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        <Plus className='w-4 h-4' />
                      </button>
                    </div>
                    {/* Reps: -/input/+ */}
                    <div className='flex items-center gap-0.5 flex-1 min-w-[7rem]'>
                      <button
                        type='button'
                        disabled={isDemo}
                        onClick={() => {
                          const newVal = Math.max(0, (set.reps || 0) - 1);
                          updateWorkoutSet(activeExerciseIndex, setIndex, 'reps', String(newVal));
                        }}
                        aria-label={`Restar una repetición a la serie ${setIndex + 1}`}
                        className={`w-10 h-11 sm:w-11 flex-shrink-0 flex items-center justify-center rounded-l-lg border ${theme.border} ${theme.hover} ${isDemo ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        <Minus className='w-4 h-4' />
                      </button>
                      <input
                        type='number'
                        placeholder={lastSet ? String(lastSet.reps) : 'Reps'}
                        aria-label={`Repeticiones de la serie ${setIndex + 1}`}
                        value={set.reps || ''}
                        onChange={(e) => {
                          updateWorkoutSet(activeExerciseIndex, setIndex, 'reps', e.target.value);
                        }}
                        min={0}
                        disabled={isDemo}
                        className={`flex-1 min-w-0 px-0.5 py-3 border-y ${theme.input} text-sm tabular-nums text-center ${isDemo ? 'opacity-60 cursor-not-allowed' : ''}`}
                      />
                      <button
                        type='button'
                        disabled={isDemo}
                        onClick={() => {
                          const newVal = (set.reps || 0) + 1;
                          updateWorkoutSet(activeExerciseIndex, setIndex, 'reps', String(newVal));
                        }}
                        aria-label={`Sumar una repetición a la serie ${setIndex + 1}`}
                        className={`w-10 h-11 sm:w-11 flex-shrink-0 flex items-center justify-center rounded-r-lg border ${theme.border} ${theme.hover} ${isDemo ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        <Plus className='w-4 h-4' />
                      </button>
                    </div>
                    {canDelete && (
                      <button
                        type='button'
                        onClick={() => removeWorkoutSet(activeExerciseIndex, setIndex)}
                        className={`hidden sm:flex w-11 h-11 flex-shrink-0 items-center justify-center rounded-md ${theme.textSecondary} ${theme.hover}`}
                        aria-label='Eliminar serie'
                      >
                        <Trash2 className='w-4 h-4' />
                      </button>
                    )}
                    {lastSet && !isConfirmed && (
                      <button
                        type='button'
                        onClick={() => applyLastSessionSet(setIndex, lastSet)}
                        className={`w-full min-h-11 px-2 py-1.5 rounded-md flex items-center justify-between gap-2 text-left text-[11px] sm:text-xs font-medium ${theme.textSecondary} ${theme.hover}`}
                      >
                        <span>
                          Última:{' '}
                          <span className={`font-bold ${theme.text}`}>
                            {lastSet.weight} kg × {lastSet.reps}
                          </span>
                        </span>
                        <span className={`rounded-md border ${theme.border} px-2 py-0.5 font-semibold`}>
                          Usar
                        </span>
                      </button>
                    )}
                  </div>
                  </SwipeableSetRow>
                  );
                })}
                {!isDemo && (
                  <button
                    type='button'
                    onClick={() => addWorkoutSet(activeExerciseIndex)}
                    className={`w-full flex items-center justify-center gap-1.5 py-3 mt-1 rounded-lg border border-dashed ${theme.border} ${theme.textSecondary} ${theme.hover} text-xs sm:text-sm font-medium`}
                  >
                    <Plus className='w-3.5 h-3.5' />
                    Agregar serie
                  </button>
                )}
              </div>
            </div>
          )}

        </div>

        <div
          className={`sticky bottom-0 z-20 rounded-t-2xl border ${theme.border} ${theme.card} backdrop-blur-md shadow-[0_-16px_40px_-28px_rgba(0,0,0,0.45)] px-4 pt-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] sm:px-6 sm:pt-5 sm:pb-6`}
        >
          {/* Rest Timer */}
          {!isDemo && (
            <div className='mb-3'>
              {restTimer === null ? (
                <div className='flex gap-2'>
                  {[60, 90, 120, 180].map(s => (
                    <button
                      key={s}
                      type='button'
                      onClick={() => {
                        initRestAudio();
                        setRestMinimized(false);
                        setRestTimer(createTimer(s));
                      }}
                      className={`flex-1 py-3 rounded-lg border ${theme.border} ${theme.hover} text-sm font-medium`}
                    >
                      {formatTime(s)}
                    </button>
                  ))}
                </div>
              ) : restTimer.left > 0 ? (
                <div className='flex items-stretch gap-2'>
                  <button
                    type='button'
                    onClick={() => setRestMinimized(false)}
                    aria-label='Ampliar descanso'
                    className={`flex-1 min-h-11 px-3 py-2 rounded-lg border ${theme.border} ${theme.hover} flex items-center gap-3`}
                  >
                    <span className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${theme.textSecondary}`}>
                      Descanso
                    </span>
                    <span className='font-mono text-lg font-bold tabular-nums'>
                      {formatTime(restTimer.left)}
                    </span>
                    <span className={`flex-1 h-1.5 rounded-full overflow-hidden ${darkMode ? 'bg-stone-700' : 'bg-stone-300'}`}>
                      <span
                        style={{ width: `${(restTimer.left / restTimer.total) * 100}%` }}
                        className='block h-full rounded-full bg-[#d97745] transition-all duration-1000'
                      />
                    </span>
                  </button>
                  <button
                    type='button'
                    onClick={stopTimer}
                    className={`w-11 min-h-11 flex items-center justify-center rounded-lg border ${theme.border} ${theme.hover}`}
                    aria-label='Detener descanso'
                  >
                    <X className='w-5 h-5' />
                  </button>
                </div>
              ) : null}
            </div>
          )}
          <div className='flex flex-row gap-2'>
            {!isDemo && (
              <button
                onClick={() => {
                  pendingSummaryRef.current = buildSessionSummary();
                  saveWorkout();
                }}
                disabled={saveState !== 'idle'}
                className={`flex-1 py-3 sm:py-4 rounded-lg font-semibold flex items-center justify-center gap-2 touch-manipulation text-sm sm:text-base whitespace-nowrap ${
                  saveState === 'saving'
                    ? 'bg-stone-600 text-white cursor-not-allowed'
                    : saveState === 'saved'
                    ? 'bg-green-700 text-white cursor-not-allowed'
                    : 'pressable bg-[#b4532a] text-white hover:bg-[#9a4523] active:bg-[#8a3d1f]'
                }`}
              >
                {saveState === 'saving' ? (
                  <Loader2 className='w-5 h-5 shrink-0 animate-spin' />
                ) : saveState === 'saved' ? (
                  <Check className='w-5 h-5 shrink-0' />
                ) : (
                  <Check className='w-5 h-5 shrink-0' />
                )}
                <span className='truncate'>
                  {saveState === 'saving'
                    ? 'Guardando...'
                    : saveState === 'saved'
                    ? 'Terminado'
                    : isEditing ? 'Actualizar' : 'Terminar'}
                </span>
              </button>
            )}
            <button
              onClick={handleClose}
              className={`${isDemo ? `flex-1 text-white hover:brightness-105 bg-gradient-to-r ${darkMode ? 'from-[#d97745] to-[#bc5f31]' : 'from-[#b4532a] to-[#d97745]'}` : `flex-none px-4 sm:px-6 border ${theme.border} ${theme.hover} ${darkMode ? 'text-red-300' : 'text-red-700'}`} py-3 sm:py-4 rounded-lg font-semibold flex items-center justify-center gap-2 touch-manipulation text-sm sm:text-base whitespace-nowrap`}
            >
              <X className='w-5 h-5 shrink-0' />
              <span className='truncate'>{isDemo ? 'Cerrar' : 'Cancelar'}</span>
            </button>
          </div>
        </div>
      </section>

      {!isDemo && restTimer && restTimer.left > 0 && !restMinimized && (
        <div
          role='dialog'
          aria-modal='true'
          aria-label='Descanso en curso'
          className='fixed inset-0 z-[120] bg-gradient-to-b from-[#b4532a] via-[#a34a24] to-[#6f2810] text-white'
        >
          <button
            type='button'
            onClick={() => setRestMinimized(true)}
            className='absolute top-[max(0.75rem,env(safe-area-inset-top))] right-[max(0.75rem,env(safe-area-inset-right))] p-2 rounded-lg bg-black/20 hover:bg-black/30 active:bg-black/40 transition-colors'
            aria-label='Minimizar descanso'
          >
            <ChevronDown className='w-7 h-7' />
          </button>

          <div className='h-full w-full flex flex-col items-center justify-center px-6 text-center'>
            <p className='text-sm sm:text-base font-semibold uppercase tracking-[0.2em] text-orange-100 mb-5'>
              Descanso
            </p>
            <p className='font-mono font-bold leading-none text-[22vw] sm:text-[120px] md:text-[160px]'>
              {formatTime(restTimer.left)}
            </p>
            <button
              type='button'
              autoFocus
              onClick={stopTimer}
              className='mt-8 min-h-11 px-6 py-3 rounded-2xl bg-black/20 hover:bg-black/30 active:bg-black/40 transition-colors font-semibold'
            >
              Detener descanso
            </button>
          </div>
        </div>
      )}
      {!isDemo && restTimer && restTimer.left === 0 && (
        <div className='fixed inset-0 z-[120] bg-green-800 text-white'>
          <button
            type='button'
            onClick={stopTimer}
            className='absolute top-[max(0.75rem,env(safe-area-inset-top))] right-[max(0.75rem,env(safe-area-inset-right))] p-2 rounded-lg bg-black/25 hover:bg-black/35 active:bg-black/45 transition-colors'
            aria-label='Cerrar timer de descanso'
          >
            <X className='w-7 h-7' />
          </button>

          <div className='h-full w-full flex flex-col items-center justify-center px-6 text-center'>
            <p className='text-sm sm:text-base font-semibold uppercase tracking-[0.2em] text-green-100 mb-6'>
              Descanso
            </p>
            <p className='font-extrabold leading-none text-[14vw] sm:text-[88px] md:text-[112px]'>
              ¡Terminado!
            </p>
          </div>
        </div>
      )}
    </>
  );
}
